<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePwaInstall } from 'dashboard/composables/usePwaInstall';

const DISMISSED_STORAGE_KEY = 'talkvia:pwa-install-dismissed-at';
const DISMISS_DURATION = 1000 * 60 * 60 * 24 * 14;
const { t } = useI18n();
const {
  canInstall,
  isInstalled,
  isInstalling,
  platform,
  install: installApp,
} = usePwaInstall();
const isVisible = ref(false);
const showIOSInstructions = ref(false);
const isIOS = computed(() => platform.value === 'ios');
const appIconPath = '/talkvia-app-192.png';
const copy = computed(() => ({
  ariaLabel: t('OPERATIONAL_HOME.INSTALL.BUTTON'),
  title: t('OPERATIONAL_HOME.INSTALL.BUTTON'),
  description: t('OPERATIONAL_HOME.INSTALL.BANNER_DESCRIPTION'),
  iosInstructions: t('OPERATIONAL_HOME.INSTALL.IOS_INSTRUCTIONS'),
  install: t('OPERATIONAL_HOME.INSTALL.BUTTON'),
  iosInstall: t('OPERATIONAL_HOME.INSTALL.HOW_TO'),
  dismiss: t('OPERATIONAL_HOME.INSTALL.LATER'),
}));
let visibilityTimer;
const wasRecentlyDismissed = () => {
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISSED_STORAGE_KEY));
    return dismissedAt && Date.now() - dismissedAt < DISMISS_DURATION;
  } catch {
    return false;
  }
};
const reveal = () => {
  const isMobile =
    window.matchMedia('(max-width: 768px)').matches ||
    /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);
  if (isMobile && !isInstalled.value && !wasRecentlyDismissed())
    isVisible.value = true;
};
watch(canInstall, available => {
  if (available) reveal();
  else if (!isIOS.value) isVisible.value = false;
});
const dismiss = () => {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, Date.now().toString());
  } catch {
    // The banner can be dismissed even when browser storage is unavailable.
  }
  isVisible.value = false;
};
const install = async () => {
  if (isIOS.value) {
    showIOSInstructions.value = true;
    return;
  }
  try {
    const outcome = await installApp();
    if (outcome === 'accepted' || outcome === 'dismissed') dismiss();
  } catch {
    dismiss();
  }
};
onMounted(() => {
  if (isIOS.value) visibilityTimer = window.setTimeout(reveal, 1500);
  else if (canInstall.value) reveal();
});
onBeforeUnmount(() => window.clearTimeout(visibilityTimer));
</script>

<template>
  <Teleport to="body">
    <section
      v-if="isVisible && !isInstalled"
      class="pwa-install-prompt fixed inset-x-3 bottom-3 z-[9999] rounded-lg border border-n-weak bg-n-solid-1 p-4 text-n-slate-12 shadow-2xl md:hidden"
      :aria-label="copy.ariaLabel"
    >
      <div class="flex items-start gap-3">
        <img
          :src="appIconPath"
          alt=""
          class="size-11 flex-shrink-0 rounded-lg"
        />
        <div class="min-w-0 flex-1">
          <h2 class="text-sm font-semibold">{{ copy.title }}</h2>
          <p class="mt-1 text-sm leading-5 text-n-slate-11">
            {{ copy.description }}
          </p>
          <p
            v-if="showIOSInstructions"
            class="mt-3 rounded-lg bg-n-alpha-2 p-3 text-sm leading-5 text-n-slate-11"
          >
            {{ copy.iosInstructions }}
          </p>
          <div class="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:brightness-95"
              :disabled="isInstalling"
              @click="install"
            >
              {{ isIOS ? copy.iosInstall : copy.install }}
            </button>
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
              @click="dismiss"
            >
              {{ copy.dismiss }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </Teleport>
</template>

<style scoped>
.pwa-install-prompt {
  padding-bottom: max(1rem, env(safe-area-inset-bottom));
}
</style>
